import { CalendarCheck, Wallet, Rocket } from "lucide-react";
import { STR } from "@/data/i18n";
import { useLang } from "@/lib/lang";
import { FadeUp, SectionHeading } from "./Shared";

const STEP_ICONS = [CalendarCheck, Wallet, Rocket];

const TABLE_ROWS = [
    { plan: "Basic", dp: "Rp 2.500.000", settle: "Rp 2.500.000" },
    { plan: "Profesional", dp: "Rp 5.000.000", settle: "Rp 5.000.000" },
    { plan: "Luxury", dp: "Rp 25.000.000", settle: "Rp 25.000.000" },
];

export const Payment = () => {
    const { lang } = useLang();
    const t = STR.payment[lang];

    return (
        <section id="pembayaran" data-testid="payment-section" className="relative py-24 md:py-32">
            <div className="mx-auto max-w-7xl px-5 md:px-8">
                <SectionHeading eyebrow={t.eyebrow} title={t.title} subtitle={t.subtitle} />

                <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                    {t.steps.map(({ title, desc }, i) => {
                        const Icon = STEP_ICONS[i];
                        return (
                            <FadeUp key={title} delay={i * 0.1}>
                                <div
                                    data-testid={`payment-step-${i + 1}`}
                                    className="card-surface relative h-full p-8 transition-[transform,border-color] duration-300 hover:-translate-y-2 hover:border-amber-400/30"
                                >
                                    <span className="absolute right-6 top-6 text-4xl font-extrabold text-white/5">
                                        {i + 1}
                                    </span>
                                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-red-600/20 to-amber-500/20 text-amber-400">
                                        <Icon className="h-6 w-6" />
                                    </div>
                                    <h3 className="mt-6 text-lg font-semibold text-slate-50">{title}</h3>
                                    <p className="mt-3 text-sm leading-relaxed text-zinc-400">{desc}</p>
                                </div>
                            </FadeUp>
                        );
                    })}
                </div>

                <FadeUp delay={0.15}>
                    <div className="card-surface mt-12 overflow-hidden p-0">
                        <div className="overflow-x-auto">
                            <table data-testid="payment-table" className="w-full min-w-[520px] text-left text-sm">
                                <thead>
                                    <tr className="border-b border-white/10 bg-white/5">
                                        <th className="px-6 py-4 font-semibold text-slate-200">{t.table.plan}</th>
                                        <th className="px-6 py-4 font-semibold text-slate-200">{t.table.dp}</th>
                                        <th className="px-6 py-4 font-semibold text-slate-200">{t.table.settle}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {TABLE_ROWS.map((row) => (
                                        <tr
                                            key={row.plan}
                                            data-testid={`payment-row-${row.plan.toLowerCase()}`}
                                            className="border-b border-white/5 transition-colors duration-200 last:border-0 hover:bg-white/5"
                                        >
                                            <td className="px-6 py-4 font-medium text-slate-100">{row.plan}</td>
                                            <td className="px-6 py-4 text-amber-400">{row.dp}</td>
                                            <td className="px-6 py-4 text-zinc-300">{row.settle}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </FadeUp>

                <FadeUp delay={0.2}>
                    <p
                        data-testid="payment-note"
                        className="mx-auto mt-8 max-w-2xl rounded-xl border border-amber-400/20 bg-amber-400/5 px-6 py-4 text-center text-sm leading-relaxed text-amber-200/90"
                    >
                        {t.notePre} <strong>{t.noteStrong}</strong> {t.notePost}
                    </p>
                </FadeUp>
            </div>
        </section>
    );
};
